import { ReactElement, ChangeEvent, useMemo, useCallback } from 'react';
import { Typography, Stack, Collapse } from '@mui/material';
import {
  ChangeCircle as ChangeStateIcon,
  ManageAccounts as ModifyAssignmentIcon,
} from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileArrowDown as ExportListIcon,
  faClone as DuplicateIcon,
  faHistory as MarkAsPendingIcon,
  faFileInvoiceDollar as BillingIcon,
} from '@fortawesome/free-solid-svg-icons';
import { useSnackbar } from 'notistack';
import moment from 'moment';

import { useAppSelector, useLazyRequest, useToggle } from '~hooks/index';
import { selectAuth } from '~store/slices/auth/index';
import { isAdminUser, isOnlyReadUser, isOnlyCreatorOrder } from '~utils/user';
import { ItemFilterV2Base } from '~services/item/types';
import {
  searchItemsToExportV2,
  markAsPendingItems,
  duplicateItems,
} from '~services/item';
import {
  ActionOrderMenu,
  DialogChangeOrderState,
  DialogModifyAssignmentOrders,
  DialogConfimation,
  DialogRebillOrders,
} from '~components/index';
import { ActionOrderMenuOptionItem } from '~components/ActionOrderMenu/types';
import { Nullable } from '~globals/types/commons';
import { PlanFeaturesTypes } from '~globals/types/enums';
import { getFeatureAvailableToDate } from '~utils/feature';
import { formatterDate } from '~utils/formatter';
import { downloadFileByRequest } from '~utils/download';

// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierFinalizedFiltersContext } from '../../../../../FinalizedFiltersContext';
import { useDashboardVariantV2CarrierPresetsContext } from '../../../../../PresetsContext';
import { useDashboardVariantV2CarrierContentFinalizedTabContext } from '../../Context';

import {
  DashboardVariantV2CarrierContentActionsContainer,
  DashboardVariantV2CarrierContentFormControlWithBackground,
  DashboardVariantV2CarrierContentFormControlCheckBox,
} from '../../../../styles';

const DashboardVariantV2CarrierContentFinalizedTabActions =
  (): ReactElement => {
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAppSelector(selectAuth);

    const isAdmin = useMemo(() => isAdminUser(user), [user]);
    const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);
    const isUserOnlyCreatorOrder = useMemo(
      () => isOnlyCreatorOrder(user),
      [user],
    );

    const {
      filters: { handleClearFilters, handleClearFilterByValues },
      forceRequest: { toggleValue: toggleForceRequest },
    } = useDashboardVariantV2CarrierFinalizedFiltersContext();

    const {
      selectedPreset: { reset: handleResetSelectedPreset },
    } = useDashboardVariantV2CarrierPresetsContext();

    const {
      selection: { selectedIds, utils: selectedIdsUtils },
      parseFiltersRequest,
      table: {
        queryParams: { pagination },
      },
      actions: { reloadDataAndResetSelectedRows, handleShowSuccessAlert },
      isOnlyTodayDelivery: {
        value: isOnlyTodayDelivery,
        toggleValue: toggleIsOnlyTodayDelivery,
      },
    } = useDashboardVariantV2CarrierContentFinalizedTabContext();

    const isEmptySelectedIds = useMemo(
      () => selectedIdsUtils.isEmpty(),
      [selectedIdsUtils],
    );

    const handleChangeIsTodayDelivery = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target ?? event.currentTarget;
        toggleIsOnlyTodayDelivery(checked);

        let expirationDateTimeFrom: Nullable<Date> = null;
        let expirationDateTimeTo: Nullable<Date> = null;

        if (checked) {
          const currentDate = moment();
          expirationDateTimeFrom = currentDate.startOf('day').toDate();
          expirationDateTimeTo = currentDate.clone().endOf('day').toDate();
        }

        handleClearFilters(false);
        handleClearFilterByValues(
          { expirationDateTimeFrom, expirationDateTimeTo },
          () => toggleForceRequest(true),
        );
        handleResetSelectedPreset();
      },
      [
        handleClearFilterByValues,
        handleClearFilters,
        handleResetSelectedPreset,
        toggleForceRequest,
        toggleIsOnlyTodayDelivery,
      ],
    );

    const [, loadingSearchToExport, , executeSearchToExport] = useLazyRequest({
      request: searchItemsToExportV2,
      withPostSuccess: (response) => {
        if (response.headers && response.data) {
          const bodyRequest = JSON.parse(
            response.config?.data as string,
          ) as ItemFilterV2Base;

          let currentDate = formatterDate(new Date(), { format: 'YYYYMMDD' });

          if (bodyRequest.dateFrom && bodyRequest.dateTo) {
            const dateFromDownload = formatterDate(bodyRequest.dateFrom, {
              format: 'YYYYMMDD',
            });

            const dateToDownload = formatterDate(bodyRequest.dateTo, {
              format: 'YYYYMMDD',
            });

            currentDate = `${dateFromDownload}-${dateToDownload}`;
          }

          const defaultFileName = `pedidos-finalizado-${currentDate}.csv`;

          downloadFileByRequest(
            response.headers,
            response.data,
            defaultFileName,
          );

          enqueueSnackbar(`Se ha exportado los pedidos correctamente`, {
            variant: 'success',
          });
        } else {
          enqueueSnackbar(
            'No se pudo exportar los pedidos, intente nuevamente',
            { variant: 'error' },
          );
        }
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo exportar los pedidos, intente nuevamente', {
          variant: 'error',
        });
      },
    });

    const handleExportItems = useCallback(async () => {
      await executeSearchToExport({
        filters: parseFiltersRequest(),
        pagination,
      });
    }, [executeSearchToExport, pagination, parseFiltersRequest]);

    const [openDialogChangeStateOrders, toggleOpenDialogChangeStateOrders] =
      useToggle(false);

    const [
      openDialogModifyAssignmentOrders,
      toggleDialogModifyAssignmentOrders,
    ] = useToggle(false);

    const [openConfirmDuplicateOrders, toggleOpenConfirmDuplicateOrders] =
      useToggle(false);

    const [
      openConfirmMarkAsPendingOrders,
      toggleOpenConfirmMarkAsPendingOrders,
    ] = useToggle(false);

    const [openDialogRebillOrders, toggleOpenDialogRebillOrders] =
      useToggle(false);

    const actionsWitSelectionMenuOptions = useMemo<ActionOrderMenuOptionItem[]>(
      () => [
        {
          label: 'Cambiar estado de pedidos',
          Icon: <ChangeStateIcon color="success" />,
          enabled: !isUserOnlyReader,
          onClick: () => toggleOpenDialogChangeStateOrders(true),
        },
        {
          label: 'Modificar asignación de pedidos',
          Icon: <ModifyAssignmentIcon color="success" />,
          enabled: !isUserOnlyReader,
          onClick: () => toggleDialogModifyAssignmentOrders(true),
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.LimitNotPro,
          },
        },
        {
          label: 'Duplicar destinos',
          Icon: <FontAwesomeIcon icon={DuplicateIcon} />,
          enabled: !isUserOnlyCreatorOrder,
          onClick: () => toggleOpenConfirmDuplicateOrders(true),
        },
        {
          label: 'Marcar como pendientes',
          Icon: <FontAwesomeIcon icon={MarkAsPendingIcon} />,
          enabled: !isUserOnlyCreatorOrder,
          onClick: () => toggleOpenConfirmMarkAsPendingOrders(true),
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.LimitNotPro,
          },
        },
        {
          label: 'Retarifar pedidos',
          Icon: <FontAwesomeIcon icon={BillingIcon} />,
          enabled: isAdmin,
          onClick: () => toggleOpenDialogRebillOrders(true),
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.MgmBillingGiver,
            availableToDate: getFeatureAvailableToDate(
              PlanFeaturesTypes.MgmBillingGiver,
            ),
          },
        },
      ],
      [
        isUserOnlyReader,
        isAdmin,
        isUserOnlyCreatorOrder,
        toggleOpenDialogChangeStateOrders,
        toggleDialogModifyAssignmentOrders,
        toggleOpenConfirmDuplicateOrders,
        toggleOpenConfirmMarkAsPendingOrders,
        toggleOpenDialogRebillOrders,
      ],
    );

    const actionsGeneralMenuOptions = useMemo<ActionOrderMenuOptionItem[]>(
      () => [
        {
          label: 'Exportar listado',
          Icon: <FontAwesomeIcon icon={ExportListIcon} />,
          loading: loadingSearchToExport,
          enabled: true,
          disabled: false,
          onClick: handleExportItems,
        },
      ],
      [loadingSearchToExport, handleExportItems],
    );

    const handleSuccessChangeOrdersState = useCallback(() => {
      toggleOpenDialogChangeStateOrders(false);

      reloadDataAndResetSelectedRows();
    }, [reloadDataAndResetSelectedRows, toggleOpenDialogChangeStateOrders]);

    const [, loadingMarkAsPendingOrders, , executeMarkAsPendingOrders] =
      useLazyRequest({
        request: markAsPendingItems,
        withPostSuccess: () => {
          handleShowSuccessAlert(
            'Destinos marcados como pendientes correctamente',
            // eslint-disable-next-line max-len
            'Los destinos seleccionados ahora están disponibles en la sección de pedidos pendientes.',
          );
          toggleOpenConfirmMarkAsPendingOrders();

          reloadDataAndResetSelectedRows();
        },
        withPostFailure: () => {
          enqueueSnackbar(
            'No se pudo marcar los pedidos como pendientes, intente nuevamente',
            { variant: 'error' },
          );
        },
      });

    const handleMarkAsPendingOrders = useCallback(() => {
      if (!isEmptySelectedIds) {
        executeMarkAsPendingOrders(selectedIds);
      }
    }, [executeMarkAsPendingOrders, isEmptySelectedIds, selectedIds]);

    const [, loadingDuplicateOrders, , executeDuplicateOrders] = useLazyRequest(
      {
        request: duplicateItems,
        withPostSuccess: () => {
          enqueueSnackbar('Destinos repetidos correctamente', {
            variant: 'success',
          });

          toggleOpenConfirmDuplicateOrders();

          reloadDataAndResetSelectedRows();
        },
        withPostFailure: () => {
          enqueueSnackbar(
            'No se pudo repetir los destinos, intente nuevamente',
            { variant: 'error' },
          );
        },
      },
    );

    const handleDuplicateOrders = useCallback(() => {
      if (!isEmptySelectedIds) {
        executeDuplicateOrders(selectedIds);
      }
    }, [executeDuplicateOrders, isEmptySelectedIds, selectedIds]);

    return (
      <DashboardVariantV2CarrierContentActionsContainer justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography component="h6" variant="h6" fontWeight="bold">
            PEDIDOS FINALIZADOS
          </Typography>

          {!isUserOnlyReader && (
            <Collapse
              in={!isEmptySelectedIds}
              timeout="auto"
              unmountOnExit
              orientation="horizontal"
            >
              <ActionOrderMenu
                options={actionsWitSelectionMenuOptions}
                buttonProps={{ label: 'Selección' }}
              />
            </Collapse>
          )}
        </Stack>

        <Stack direction="row" spacing={2}>
          <DashboardVariantV2CarrierContentFormControlWithBackground
            control={
              <DashboardVariantV2CarrierContentFormControlCheckBox
                checked={isOnlyTodayDelivery}
                onChange={handleChangeIsTodayDelivery}
              />
            }
            label="Solo envíos que vencen hoy"
          />

          {!isUserOnlyReader && (
            <ActionOrderMenu options={actionsGeneralMenuOptions} />
          )}
        </Stack>

        <DialogChangeOrderState
          open={openDialogChangeStateOrders}
          onClose={() => toggleOpenDialogChangeStateOrders(false)}
          itemsIds={selectedIds}
          onChangeSuccess={handleSuccessChangeOrdersState}
        />

        {openDialogModifyAssignmentOrders && (
          <DialogModifyAssignmentOrders
            open={openDialogModifyAssignmentOrders}
            onClose={() => toggleDialogModifyAssignmentOrders(false)}
            itemIds={selectedIds}
            onSuccessModify={() => reloadDataAndResetSelectedRows()}
          />
        )}

        <DialogConfimation
          open={openConfirmDuplicateOrders}
          description="Los destinos seleccionados serán repetidos"
          onConfirm={handleDuplicateOrders}
          onCancel={() => toggleOpenConfirmDuplicateOrders(false)}
          loadingConfirm={loadingDuplicateOrders}
        />

        <DialogConfimation
          open={openConfirmMarkAsPendingOrders}
          description="Los destinos seleccionados serán marcados como pendientes"
          onConfirm={handleMarkAsPendingOrders}
          onCancel={() => toggleOpenConfirmMarkAsPendingOrders()}
          loadingConfirm={loadingMarkAsPendingOrders}
        />

        <DialogRebillOrders
          open={openDialogRebillOrders}
          onClose={() => toggleOpenDialogRebillOrders(false)}
          itemIds={selectedIds}
          onSuccessSubmit={() => reloadDataAndResetSelectedRows()}
          excludeMode="manual"
        />
      </DashboardVariantV2CarrierContentActionsContainer>
    );
  };

export default DashboardVariantV2CarrierContentFinalizedTabActions;
